// src/components/PuzzlePage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, doc, updateDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImageAndGetURL } from '../utils/uploadImage';
import VictoryOverlay from './VictoryOverlay';
import emailjs from 'emailjs-com';
import '../utils/puzzleBackground.css';
import { playClick } from '../utils/sound';

function PuzzlePage({ image, message, gridSize, fontStyle, resetForm }) {
  const [slots, setSlots] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [recipient, setRecipient] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [agreedToEmailPolicy, setAgreedToEmailPolicy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!image) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = image;

    img.onload = () => {
      const ratio = img.width / img.height;
      setAspectRatio(ratio);

      const canvasWidth = 400;
      const canvasHeight = canvasWidth / ratio;
      const pieceWidth = canvasWidth / gridSize;
      const pieceHeight = canvasHeight / gridSize;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const pieces = [];
      for (let y = 0; y < gridSize; y++) {
        for (let x = 0; x < gridSize; x++) {
          const pieceCanvas = document.createElement('canvas');
          pieceCanvas.width = pieceWidth;
          pieceCanvas.height = pieceHeight;
          const pctx = pieceCanvas.getContext('2d');
          pctx.drawImage(
            canvas,
            x * pieceWidth,
            y * pieceHeight,
            pieceWidth,
            pieceHeight,
            0,
            0,
            pieceWidth,
            pieceHeight
          );
          pieces.push({
            id: y * gridSize + x,
            img: pieceCanvas.toDataURL(),
          });
        }
      }
      const scrambled = [...pieces].sort(() => Math.random() - 0.5);
      setSlots(scrambled);
    };
  }, [image, gridSize]);

  const handleSwap = (index) => {
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else {
      const newSlots = [...slots];
      [newSlots[selectedIndex], newSlots[index]] = [newSlots[index], newSlots[selectedIndex]];
      setSlots(newSlots);
      setSelectedIndex(null);

      if (newSlots.every((p, i) => p.id === i)) {
        setCompleted(true);
      }
    }
  };

  const canvasWidth = 400;
  const pieceWidth = canvasWidth / gridSize;
  const pieceHeight = pieceWidth / aspectRatio;

  const sendPuzzleEmail = async () => {
    if (!recipient || !recipientEmail || !senderName || !senderEmail || !image || !message || !agreedToEmailPolicy) {
      alert('All fields are required and consent must be given.');
      return;
    }

    try {
      const puzzleData = {
        message,
        gridSize,
        isPublic,
        senderName,
        senderEmail,
        recipient,
        recipientEmail,
        createdAt: new Date(),
      };

      const docRef = await addDoc(collection(db, 'puzzles'), puzzleData);
      const imageURL = await uploadImageAndGetURL(image, docRef.id);
      await updateDoc(doc(db, 'puzzles', docRef.id), { image: imageURL });

      const link = `${window.location.origin}/play/${docRef.id}`;

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          recipient_name: recipient,
          recipient_email: recipientEmail,
          sender_name: senderName,
          sender_email: senderEmail,
          puzzle_link: link,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      alert('✅ Puzzle sent!');
      navigate('/ThankYou');
    } catch (err) {
      console.error('Send failed:', err);
      alert('❌ Failed to send puzzle.');
    }
  };

  return (
    <>
      <div className="puzzle-bg-container">
        {[...Array(36)].map((_, i) => {
          const row = Math.floor(i / 6);
          const col = i % 6;
          return (
            <div
              key={i}
              className="puzzle-bg-tile"
              style={{
                backgroundPosition: `${(col / 5) * 100}% ${(row / 5) * 100}%`
              }}
            />
          );
        })}
      </div>

      <div style={{
        padding: '2rem',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(2px)',
        borderRadius: '12px',
        margin: '2rem auto',
        maxWidth: '900px'
      }}>
        <h2 style={{ fontFamily: fontStyle }}>Puzzle Preview</h2>

        {showPreview && (
          <div style={{ margin: '1rem 0' }}>
            <h3>Click a piece, then click another to swap them.</h3>
            <h3>Complete the puzzle to unlock the message.</h3>
            <img
              src={image}
              alt="Preview"
              style={{
                maxWidth: '100%',
                height: 'auto',
                width: `${gridSize * 100}px`,
                borderRadius: '4px',
                border: '1px solid #ccc',
                opacity: 0.5,
              }}
            />
          </div>
        )}

        <button
          className="button-click"
          onClick={() => {
            playClick();
            setShowPreview(!showPreview);
          }}
          style={{ marginBottom: '1rem' }}
        >
      {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
{/*The grid code starts here */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridSize}, ${pieceWidth}px)`,
          gridTemplateRows: `repeat(${gridSize}, ${pieceHeight}px)`,
          gap: completed ? '0' : '2px',
          border: completed ? 'none' : '2px solid #ccc',
          justifyContent: 'center',
          margin: '0 auto',
          marginBottom: '2rem',
          transition: 'all 0.3s ease',
          width: `${pieceWidth * gridSize}px`,
          height: `${pieceHeight * gridSize}px`
        }}>
          {slots.map((piece, index) => (
            <div
              key={index}
              onClick={() => handleSwap(index)}
              style={{
                width: pieceWidth,
                height: pieceHeight,
                border: completed ? 'none' : '1px solid #999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <img
                src={piece.img}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>

        {completed && (
          <>
            <VictoryOverlay />
            <div
              style={{
                margin: '2rem auto 1rem',
                fontFamily: fontStyle,
                fontSize: '1.5rem',
                textAlign: 'center',
                whiteSpace: 'pre-line',
                maxWidth: '80%',
                color: '#333'
              }}
            >
              {message}
            </div>
          </>
        )}

      {/* FORM stays visible at all times */}
      
    <div id="senderForm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendPuzzleEmail();
        }}

        style={{ maxWidth: '500px', marginInline: 'auto', textAlign: 'left' }}
      >
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="senderName">Your Name or Business Name:</label>
          <input
            id="senderName"
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="e.g. Jane Doe or PuzzleCo"
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="senderEmail">Your Contact Email:</label>
          <input
            id="senderEmail"
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="you@example.com"
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="checkbox">
            <input
              id="checkBox"
              type="checkbox"
              checked={agreedToEmailPolicy}
              onChange={() => setAgreedToEmailPolicy(!agreedToEmailPolicy)}
            />
            {' '}I agree to receive email notifications related to my puzzle gift.
          </label>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="recipient">Recipient Name:</label>
          <input
            id="recipient"
            type="text"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="e.g. Alice"
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="recipientEmail">Recipient Email:</label>
          <input
            id="recipientEmail"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="e.g. alice@example.com"
            required
            style={{ width: '100%', padding: '0.5rem' }}
          />
        </div>

        <div>  
          <label htmlFor="checkBox">
            <input
              id="checkBox"
              type="checkbox"
              checked={isPublic}
              onChange={() => setIsPublic(!isPublic)}
            />
            {' '}Make this puzzle public
          </label>
        </div>

        <div>
          <button
            className="button-click"
            type="submit"
            onClick={(e) => {
              e.preventDefault();      // Prevent default form submission
              playClick();             // Play click sound
              sendPuzzleEmail();       // Trigger the email function
            }}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              transition: 'transform 0.1s ease',
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Send Puzzle
          </button>
          </div>
      </form>
      </div>
      
    </div>
    </>
  );
}

export default PuzzlePage;
